import pclnRequest from '@pcln/request'
import { AxiosResponse } from 'axios'
import config from 'isomorphic-config'
import {
  SMS_SUBMIT_NO_EMAIL_MUTATION,
  SMS_SUBMIT_WITH_EMAIL_MUTATION
} from '@/shared-utils/queries/smsOptIn'
import { GraphQLResponse } from '@/types'
import analytics from '@/shared-utils/analytics'
import { DisclaimerBaseParams } from './useSmsDisclaimerQueryArgs'

type SmsSubmissionStatus = 'PENDING' | 'ERROR' | 'BAD_REQUEST'

export type SmsSubscriptionByPhoneNumber = {
  smsSubscriptionByPhoneNumber: {
    message: string
    status: SmsSubmissionStatus
  }
}

export type SmsSubscriptionByEmailAddress = {
  smsSubscriptionByEmailAddress: {
    message: string
    status: SmsSubmissionStatus
  }
}

export type SmsPhone = {
  phoneCountryCode: string
  phoneNumber: string
}

type SubmitSmsOptIn = {
  emailAddress?: string
  formData: SmsPhone
  disclaimerText: string
  baseParams: DisclaimerBaseParams
  setSignUpState: (signupState: string) => void
  setSignUpSubmitFailureMessage: (
    signUpSubmitFailureMessage: string | undefined
  ) => void
  clientGUID: string
}

function isEmailResponse(
  data: SmsSubscriptionByPhoneNumber | SmsSubscriptionByEmailAddress,
  emailAddress?: string
): data is SmsSubscriptionByEmailAddress {
  return !!emailAddress
}

const fireAnalytics = (label: string) => {
  analytics.fireEvent({
    action: 'sms signup',
    category: 'pcln_home',
    label
  })
}

export async function handleSubmit({
  emailAddress,
  formData,
  disclaimerText,
  baseParams,
  setSignUpState,
  setSignUpSubmitFailureMessage,
  clientGUID
}: SubmitSmsOptIn) {
  setSignUpState('SIGN_UP_IN_PROGRESS')

  const createSubmission = () => {
    return emailAddress
      ? {
          emailAddress,
          phoneCountryCode: formData.phoneCountryCode,
          phoneNumber: formData.phoneNumber,
          subscriptionBaseParam: {
            subscriptionTypeId: 1,
            disclaimerText,
            subscriptionStatus: true,
            placement: baseParams.disclaimerBaseParam.placement,
            appCode: baseParams.disclaimerBaseParam.appCode,
            plfCode: baseParams.disclaimerBaseParam.PLFCode
          }
        }
      : {
          phoneCountryCode: formData.phoneCountryCode,
          phoneNumber: formData.phoneNumber,
          subscriptionBaseParam: {
            subscriptionTypeId: 1,
            disclaimerText,
            subscriptionStatus: true,
            placement: baseParams.disclaimerBaseParam.placement,
            appCode: baseParams.disclaimerBaseParam.appCode,
            plfCode: baseParams.disclaimerBaseParam.PLFCode
          }
        }
  }

  try {
    const { url, timeout } = config.client['pcln-graph']
    const response: AxiosResponse<
      GraphQLResponse<
        SmsSubscriptionByPhoneNumber | SmsSubscriptionByEmailAddress
      >
    > = await pclnRequest({
      url,
      timeout,
      method: 'POST',
      data: {
        query: emailAddress
          ? SMS_SUBMIT_WITH_EMAIL_MUTATION
          : SMS_SUBMIT_NO_EMAIL_MUTATION,
        variables: createSubmission()
      }
    })

    const { errors, data } = response.data
    let status = ''
    let message = ''

    if (data) {
      status = isEmailResponse(data, emailAddress)
        ? data.smsSubscriptionByEmailAddress?.status
        : data.smsSubscriptionByPhoneNumber?.status
      message = isEmailResponse(data, emailAddress)
        ? data.smsSubscriptionByEmailAddress?.message
        : data.smsSubscriptionByPhoneNumber?.message
    }
    if (Array.isArray(errors) && errors.length > 0) {
      analytics.logError({
        message: `Failed to enroll for SMS subscription for cGuid: ${clientGUID}  Error status: ${status}, Error message: ${message}`
      })

      fireAnalytics('invalid')
      setSignUpState('SIGN_UP_ERROR')
      return
    }

    if (status === 'BAD_REQUEST') {
      analytics.logError({
        message: `Failed to enroll for SMS subscription for cGuid: ${clientGUID}  Error status: ${status}, Error message: ${message}`
      })

      setSignUpSubmitFailureMessage(message)
      fireAnalytics('invalid')
      setSignUpState('NOT_SIGNED_UP')
      return
    }

    if (status !== 'SUCCESS' && status !== 'PENDING') {
      analytics.logError({
        message: `Failed to enroll for SMS subscription for cGuid: ${clientGUID}  Error status: ${status}, Error message: ${message}`
      })

      fireAnalytics('invalid')
      setSignUpState('SIGN_UP_ERROR')
      return
    }
    // TODO remove success log in seti cleanup. seti: HP_SMS_COMPONENT
    analytics.log({
      message: `Successful SMS subscription for cGuid: ${clientGUID} status: ${status}`
    })

    fireAnalytics('valid')
    setSignUpState('SIGNED_UP')
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : ((error as any).toString() as string)
    analytics.logError({
      message: `Failed to enroll for SMS subscription for cGuid: ${clientGUID} Error message: ${errorMessage}`
    })

    fireAnalytics('invalid')
    setSignUpState('SIGN_UP_ERROR')
  }
}
